import { ComponentProps, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';

import { formatBytes } from '~/shared/utils/formatNumber';
import { GraphResponse } from '~/shared/types/api';

import { htmlLegendPlugin } from './htmlLegendPlugin';
import Legends from './Legends';
import { ExternalTooltipHandler } from './ExternalTooltip';

export default function LineChartImpl({
  data,
  activeFilter,
}: {
  data: GraphResponse['utilizationGraph'];
  activeFilter: string;
}) {
  const [visibleDatasets, setVisibleDatasets] = useState({
    utilized: true,
    size: true,
    datafyDiskUsed: true,
  });

  const toggleDatasetVisibility = (datasetKey: keyof typeof visibleDatasets) => {
    setVisibleDatasets((prevState) => ({
      ...prevState,
      [datasetKey]: !prevState[datasetKey],
    }));
  };

  const chartData = {
    labels: data?.map((d) => d.ts?.toString()),
    datasets: [
      visibleDatasets.utilized && {
        data: data!.map((d) => d.utilized),
        borderColor: '#06D727',
        backgroundColor: '#06D727',
      },
      visibleDatasets.size && {
        data: data!.map((d) => d.size),
        borderColor: '#2151F5',
        backgroundColor: '#2151F5',
      },
      visibleDatasets.datafyDiskUsed && {
        data: data!.map((d) => d.datafyDiskUsed),
        borderColor: '#A020F0',
        backgroundColor: '#A020F0',
      },
    ].filter(Boolean) as ComponentProps<typeof Line>['data']['datasets'],
  };
  const allValues = data!
    .flatMap(({ utilized, size, datafyDiskUsed }) => [utilized!, size!, datafyDiskUsed!])
    .filter((value): value is number => value !== undefined && value !== null);

  const minOverall = Math.min(...allValues);
  const maxOverall = Math.max(...allValues);
  const midOverall = (maxOverall + minOverall) / 2;

  const range = maxOverall - minOverall;

  const yMax = maxOverall + 0.1 * range;
  const yMin = minOverall - 0.1 * range;

  const options: ComponentProps<typeof Line>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        display: false,
      },
      tooltip: {
        enabled: false,
        external: ExternalTooltipHandler,
      },
    },

    scales: {
      y: {
        border: { dash: [4, 4] },
        max: yMax,
        min: yMin,
        afterBuildTicks: (axis) =>
          (axis.ticks = [
            minOverall,
            (minOverall + midOverall) / 2,
            midOverall,
            (midOverall + maxOverall) / 2,
            maxOverall,
          ].map((v) => ({
            value: v,
          }))),
        ticks: {
          callback: (value) => formatBytes(Number(value)),
          color: '#949EAC',
          font: {
            size: 16,
            family: 'Inter',
            lineHeight: 1.25,
            weight: 500,
          },
        },
      },
      x: {
        ticks: {
          autoSkip: true,
          callback: (_tickValue, index) => {
            const isWithin24hrs = isWithin24Hours((chartData?.labels || ['']) as string[]);

            return format(
              parseISO(chartData?.labels?.[index] as string),
              activeFilter?.includes('hour') || activeFilter?.includes('day') || isWithin24hrs
                ? 'HH:mm'
                : 'yyyy/M/d'
            );
          },
          maxTicksLimit: 8,
          color: '#949EAC',
          font: {
            size: 16,
            family: 'Inter',
            lineHeight: 1.25,
            weight: 500,
          },
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <Line
        className="max-h-[276px] max-w-full"
        options={options}
        data={chartData}
        plugins={[htmlLegendPlugin]}
      />

      <Legends onLabelToggle={toggleDatasetVisibility} visibilityStatus={visibleDatasets} />
    </div>
  );
}

function isWithin24Hours(timestamps: string[]): boolean {
  if (!timestamps || timestamps.length === 0) {
    return true;
  }

  const dates = timestamps.map((timestamp) => new Date(timestamp));

  const minDate = Math.min(...dates.map((date) => date.getTime()));
  const maxDate = Math.max(...dates.map((date) => date.getTime()));

  const twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000;
  return maxDate - minDate <= twentyFourHoursInMilliseconds;
}
